import { AutoFormFields } from 'lib/LegacyForm';
import { Option } from 'lib/types';
import { ContentModule, ContentModuleType, IAwardsModule } from '../models/ContentModule';
import { createModuleBasicForm } from './createModuleBasicForm';

export interface AwardsModuleAdditionalData {
    perkOptions?: Option[];
    selectedIncludePerkIds?: Option[];
    selectedExcludePerkIds?: Option[];
}

export const createModuleAwardsForm: (
    additionalData?: AwardsModuleAdditionalData
) => AutoFormFields = additionalData => {
    const allOptions = additionalData?.perkOptions ?? [];
    const hasIncludedPerks = (additionalData?.selectedIncludePerkIds?.length ?? 0) > 0;
    const hasExcludedPerks = (additionalData?.selectedExcludePerkIds?.length ?? 0) > 0;

    return {
        ...createModuleBasicForm,
        perkFilterLabel: {
            type: 'label',
            label: 'Perk Filtering (Optional)',
            description:
                'Your app must be on the latest build version. Please raise a ticket to request this.'
        },
        includePerkIds: {
            label: 'Include Perks',
            type: 'multiSelect',
            options: allOptions,
            disabled: hasExcludedPerks,
            description: hasExcludedPerks
                ? 'Cannot include perks when exclude perks is set. Clear exclude perks to use this field.'
                : 'Select perks to include in this module. If empty, all perks will be shown (unless excluded).'
        },
        excludePerkIds: {
            label: 'Exclude Perks',
            type: 'multiSelect',
            options: allOptions,
            disabled: hasIncludedPerks,
            description: hasIncludedPerks
                ? 'Cannot exclude perks when include perks is set. Clear include perks to use this field.'
                : 'Select perks to exclude from this module.'
        }
    };
};

export function isAwardsModule(module: ContentModule): module is IAwardsModule {
    return module.type === ContentModuleType.AWARDS;
}

export function transformModuleToAwardsFormFields(module: ContentModule, perkOptions?: Option[]): any {
    if (isAwardsModule(module)) {
        const includePerkIds = module.properties?.includePerkIds ?? [];
        const excludePerkIds = module.properties?.excludePerkIds ?? [];

        // Helper to map IDs to Option objects with proper labels
        const mapIdsToOptions = (ids: string[]): Option[] =>
            ids.map(id => {
                const option = perkOptions?.find(opt => opt.value === id);
                return option ?? { value: id, label: id };
            });

        return {
            ...module,
            secondaryActionTitle: module.secondaryAction ? module.secondaryAction.title : undefined,
            secondaryActionAction: module.secondaryAction ? module.secondaryAction.action : undefined,
            // Transform string[] to Option[] format for multiSelect
            includePerkIds: mapIdsToOptions(includePerkIds),
            excludePerkIds: mapIdsToOptions(excludePerkIds)
        };
    }
    return module;
}

function getProperties(formData: { [key: string]: any }) {
    // Extract values from Option[] format back to string[]
    const includePerkIds = Array.isArray(formData?.includePerkIds)
        ? formData.includePerkIds.map((opt: Option | string) => (typeof opt === 'string' ? opt : opt.value))
        : [];
    const excludePerkIds = Array.isArray(formData?.excludePerkIds)
        ? formData.excludePerkIds.map((opt: Option | string) => (typeof opt === 'string' ? opt : opt.value))
        : [];

    // Always return an object (even if empty) so the full payload overwrites existing properties
    return {
        ...(includePerkIds.length > 0 && { includePerkIds }),
        ...(excludePerkIds.length > 0 && { excludePerkIds })
    };
}

export function transformAwardsFormFieldToModuleFields(
    fieldName: string,
    value: any,
    formData: { [key: string]: any }
): { name: string; value: any }[] {
    switch (fieldName) {
        case 'secondaryActionTitle':
            return [
                { name: 'secondaryAction', value: { action: formData.secondaryActionAction, title: value } }
            ];
        case 'secondaryActionAction':
            if (!value) {
                return [{ name: 'secondaryAction', value: undefined }];
            }
            return [
                {
                    name: 'secondaryAction',
                    value: { action: value, title: formData.secondaryActionTitle }
                }
            ];
        case 'includePerkIds':
            return [
                {
                    name: 'properties',
                    value: getProperties({ ...formData, includePerkIds: value })
                }
            ];
        case 'excludePerkIds':
            return [
                {
                    name: 'properties',
                    value: getProperties({ ...formData, excludePerkIds: value })
                }
            ];
        default:
            return [{ value, name: fieldName }];
    }
}
